import { useEffect, useRef } from "react";
import Gantt from "frappe-gantt";
import "@/styles/frappe-gantt.vendor.css";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { invalidateForTask } from "@/lib/useTaskMutations";
import { parseDateLocal } from "@/lib/format";
import { autoSchedule as runAutoSchedule } from "@/lib/autoSchedule";
import { toast } from "@/components/Toast";

export type GanttViewMode = "Day" | "Week" | "Month" | "Year";

// Minimal shape the chart needs (a TaskRow satisfies it).
type GanttTask = {
  id: string;
  title: string;
  parentTaskId?: string | null;
  status: string;
  assigneeIds: string[];
  startDate: string | null;
  dueDate: string | null;
  startLocked?: boolean | number;
};
type Edge = { blockerId: string; blockedId: string };
type Milestone = { id: string; name: string; dueDate: string | null };

/**
 * Frappe-Gantt-backed timeline (task_lyutb4). Replaces the bespoke SVG
 * TaskTimeline with frappe-gantt + custom overlays:
 *  - dependency arrows (from the project's blocker→blocked edges)
 *  - critical-path highlight (toggleable; bars get a .bar-critical class)
 *  - milestones (rendered as marker tasks)
 *  - assignee shown in each bar's popup (resource context)
 * Drag-to-reschedule persists the new start/due dates.
 */
function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

// frappe-gantt's `popup` must return an HTML string, so escape any
// user-controlled value (task title) before interpolating it.
function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function FrappeGanttView({
  projectId,
  tasks,
  edges,
  milestones,
  users,
  criticalIds,
  showCritical,
  viewMode,
  onViewModeChange,
  depMode = false,
  onSelectTask,
  autoSchedule = false,
  tasksMeta,
}: {
  projectId: string;
  tasks: GanttTask[];
  edges: Edge[];
  milestones: Milestone[];
  users: Map<string, { name: string; image: string | null }>;
  criticalIds: Set<string>;
  showCritical: boolean;
  viewMode: GanttViewMode;
  onViewModeChange: (m: GanttViewMode) => void;
  // When depMode is on, clicking a bar selects that task for the dependency
  // editor (via onSelectTask) instead of navigating to its detail page.
  depMode?: boolean;
  onSelectTask?: (taskId: string) => void;
  // When on, dragging a task cascades its dependents forward (task_55l7tk).
  autoSchedule?: boolean;
  // Portfolio mode (task_61bjlu): per-task project meta. When present, bars are
  // labelled with their project, coloured by it, and a drag invalidates the
  // task's own project rather than the single `projectId` prop.
  tasksMeta?: Map<
    string,
    { projectId: string; projectName: string; color: string | null }
  >;
}) {
  const elRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const metaRef = useRef(tasksMeta);
  metaRef.current = tasksMeta;

  const patch = useMutation({
    mutationFn: (v: { taskId: string; startDate: string; dueDate: string }) =>
      api.patchTask(v.taskId, { startDate: v.startDate, dueDate: v.dueDate }),
    onSettled: (_d, _e, v) =>
      invalidateForTask(qc, {
        projectId: metaRef.current?.get(v.taskId)?.projectId ?? projectId,
        taskId: v.taskId,
      }),
  });

  // Auto-schedule cascade: persist each shifted dependent sequentially (D1
  // write-burst discipline), then refresh the project's tasks so bars redraw.
  const cascade = useMutation({
    mutationFn: async (
      shifts: { id: string; startDate: string; dueDate: string }[],
    ) => {
      for (const s of shifts) {
        await api.patchTask(s.id, {
          startDate: s.startDate,
          dueDate: s.dueDate,
        });
      }
      return shifts.length;
    },
    onSuccess: (n) => {
      invalidateForTask(qc, { projectId });
      if (n > 0)
        toast.success(`Rescheduled ${n} dependent task${n === 1 ? "" : "s"}.`);
    },
  });

  // Keep the latest handlers in refs so the effect doesn't re-init the chart
  // on every render (frappe-gantt owns the DOM; re-init is expensive).
  const patchRef = useRef(patch);
  patchRef.current = patch;
  const navRef = useRef(navigate);
  navRef.current = navigate;
  // Latest dep-mode + select handler in refs so toggling them doesn't re-init
  // the chart (frappe-gantt owns the DOM; re-init is expensive).
  const depModeRef = useRef(depMode);
  depModeRef.current = depMode;
  const onSelectTaskRef = useRef(onSelectTask);
  onSelectTaskRef.current = onSelectTask;
  const autoSchedRef = useRef(autoSchedule);
  autoSchedRef.current = autoSchedule;
  const cascadeRef = useRef(cascade);
  cascadeRef.current = cascade;

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;

    // Predecessor map: task → ids it depends on (blocker → blocked edge).
    const deps = new Map<string, string[]>();
    for (const e of edges) {
      const arr = deps.get(e.blockedId) ?? [];
      arr.push(e.blockerId);
      deps.set(e.blockedId, arr);
    }

    // Only top-level tasks that have at least a due date can be plotted.
    const top = tasks.filter((t) => t.parentTaskId == null);
    const progressFor = (s: string) =>
      s === "done" ? 100 : s === "doing" ? 50 : 0;

    const ganttTasks = top
      .map((t) => {
        const start = t.startDate ?? t.dueDate;
        const end = t.dueDate ?? t.startDate;
        if (!start || !end) return null;
        const assignee = t.assigneeIds[0]
          ? (users.get(t.assigneeIds[0])?.name ?? "")
          : "";
        const meta = tasksMeta?.get(t.id);
        // Portfolio mode labels with the project; single-project mode with the
        // assignee.
        const label = meta
          ? `${meta.projectName} — ${t.title}`
          : assignee
            ? `${t.title} · ${assignee}`
            : t.title;
        const cls = [
          showCritical && criticalIds.has(t.id) ? "bar-critical" : "",
        ]
          .filter(Boolean)
          .join(" ");
        return {
          id: t.id,
          name: label,
          start,
          end,
          progress: progressFor(t.status),
          dependencies: (deps.get(t.id) ?? []).join(","),
          custom_class: cls,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x != null);

    // Milestones as marker tasks (zero-length bars with a diamond style).
    const msTasks = milestones
      .filter((m) => m.dueDate)
      .map((m) => ({
        id: `ms_${m.id}`,
        name: `◆ ${m.name}`,
        start: m.dueDate!,
        end: m.dueDate!,
        progress: 0,
        dependencies: "",
        custom_class: "bar-milestone",
      }));

    const all = [...ganttTasks, ...msTasks];
    if (all.length === 0) {
      el.innerHTML =
        '<p class="t-muted" style="padding:24px">No tasks have a date yet — add a due date (or run Level resources) to see bars here.</p>';
      return;
    }

    // Re-apply per-project bar tints (portfolio mode). Called after every
    // (re)render, since frappe-gantt rebuilds the bars on update_options.
    const paint = () => {
      if (!tasksMeta) return;
      for (const [id, m] of tasksMeta) {
        if (!m.color) continue;
        const bar = el.querySelector<SVGElement>(
          `.bar-wrapper[data-id="${CSS.escape(id)}"] .bar`,
        );
        if (bar) bar.style.fill = m.color;
      }
    };

    let ro: ResizeObserver | null = null;
    try {
      const g = new Gantt(el, all, {
        view_mode: viewMode,
        view_mode_select: true,
        today_button: true,
        infinite_padding: false,
        readonly_progress: true,
        popup_on: "click",
        move_dependencies: false,
        on_click: (task: { id: string }) => {
          if (task.id.startsWith("ms_")) return;
          // In dependency-edit mode, a bar click picks that task for the
          // editor panel instead of leaving the page.
          if (depModeRef.current) {
            onSelectTaskRef.current?.(task.id);
            return;
          }
          navRef.current({ to: "/tasks/$taskId", params: { taskId: task.id } });
        },
        on_date_change: (task: { id: string }, start: Date, end: Date) => {
          if (task.id.startsWith("ms_")) return;
          const ns = ymd(start);
          const nd = ymd(end);
          patchRef.current.mutate({
            taskId: task.id,
            startDate: ns,
            dueDate: nd,
          });
          // Cascade dependents forward when auto-schedule is on. `tasks`/`edges`
          // are the effect's current snapshot (both are effect deps).
          if (autoSchedRef.current) {
            const shifts = runAutoSchedule(tasks, edges, task.id, ns, nd);
            if (shifts.length) cascadeRef.current.mutate(shifts);
          }
        },
        on_view_change: (mode: { name?: string } | string) => {
          const name = typeof mode === "string" ? mode : mode?.name;
          if (name) onViewModeChange(name as GanttViewMode);
        },
        popup: (ctx: { task?: { id: string; name: string } }) => {
          const t = ctx.task;
          if (!t) return false;
          const orig = top.find((x) => x.id === t.id);
          const due = orig?.dueDate
            ? (parseDateLocal(orig.dueDate)?.toLocaleDateString() ?? "")
            : "";
          return `<div class="gantt-pop"><b>${esc(t.name)}</b>${
            due ? `<div>Due ${esc(due)}</div>` : ""
          }</div>`;
        },
      });
      paint();

      // Stretch the timeline columns so the chart fills the container width,
      // and reflow when the container resizes — frappe-gantt otherwise renders
      // at one fixed size and ignores later size changes. Never shrink below
      // the view's natural column width; cap so a very short range doesn't
      // produce absurdly wide columns.
      const MAX_COL = 240;
      const baseColWidth = g.options?.column_width ?? 0;
      const fitWidth = () => {
        const cw = el.clientWidth;
        const cols = g.dates?.length ?? 0;
        if (!cw || !cols || !baseColWidth) return;
        const target = Math.min(
          MAX_COL,
          Math.max(baseColWidth, Math.floor(cw / cols)),
        );
        if (target !== (g.options?.column_width ?? baseColWidth)) {
          g.update_options({ column_width: target });
          paint();
        }
      };
      fitWidth();

      let raf = 0;
      ro = new ResizeObserver(() => {
        // Coalesce a drag-resize burst into one reflow per frame.
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(fitWidth);
      });
      ro.observe(el);
    } catch {
      el.innerHTML =
        '<p class="t-muted" style="padding:24px">Couldn’t render the timeline.</p>';
    }

    return () => {
      // frappe-gantt owns this subtree; clear it on teardown/re-render.
      ro?.disconnect();
      el.innerHTML = "";
    };
    // Re-init when the data or critical/view toggles change.
  }, [
    tasks,
    edges,
    milestones,
    users,
    criticalIds,
    showCritical,
    viewMode,
    onViewModeChange,
    tasksMeta,
  ]);

  return <div className="frappe-gantt-wrap" ref={elRef} />;
}
